import { useState } from 'react';
import { Alert, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { IOpoAddress, IOpoAddressRequest, OpoAddressType } from '@nkr/models/opo-address';
import useOpoAddresses from '@nkr/store/opo/hooks/use-opo-addresses';
import { SettingsPageWrapper } from '../styles';
import { SettingsTableCard } from '../table-styles';
import ConfirmModal from '../confirm-modal';
import AddressModal from './address-modal';

const OpoAddresses = () => {
    const { data, loading, error, save, remove, validateAddress } = useOpoAddresses();
    const [editing, setEditing] = useState<{ show: boolean, address: IOpoAddress | null }>({ show: false, address: null });
    const [deleting, setDeleting] = useState<IOpoAddress | null>(null);
    const [actionError, setActionError] = useState('');

    if (loading) {
        return null;
    }

    const canManage = data?.canManage == true;
    const usedTypes = data ? data.addresses.map(x => x.typeId) : [];
    const availableTypes = data ? data.addressTypes.filter(x => usedTypes.indexOf(x.id) < 0) : [];
    const missingTypes = availableTypes.map(x => x.description);

    const handleSave = async (addressId: number | null, request: IOpoAddressRequest) => {
        const result = await save(addressId, request);

        if (result.success) {
            setEditing({ show: false, address: null });
            toast.success('Address saved');
        }

        return result;
    };

    const handleDelete = () => {
        const address = deleting;

        setActionError('');
        setDeleting(null);

        remove(address.id).then(result => {
            if (result.success) {
                toast.success(`${address.typeName} address deleted`);
            } else {
                setActionError(result.message);
            }
        });
    };

    return (
        <SettingsPageWrapper style={{ maxWidth: 1100 }}>
            {(error || actionError) && (
                <Alert bsStyle='danger' onDismiss={actionError ? () => setActionError('') : undefined}>{error || actionError}</Alert>
            )}

            {data && !canManage && (
                <div className='settings-notice'>Only OPO administrators can add or change addresses.</div>
            )}

            {data && (
                <SettingsTableCard>
                    <div className='table-card-header'>
                        <h4>Addresses</h4>
                        {missingTypes.length > 0 && (
                            <span className='count'>Missing: {missingTypes.join(', ')}</span>
                        )}
                        {canManage && (
                            <button type='button' className='btn btn-primary' disabled={availableTypes.length == 0} onClick={() => setEditing({ show: true, address: null })}>Add Address</button>
                        )}
                    </div>

                    <div className='table-card-body'>
                        <Table condensed>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left' }}>Type</th>
                                    <th style={{ textAlign: 'left' }}>Name</th>
                                    <th style={{ textAlign: 'left' }}>Address</th>
                                    <th style={{ textAlign: 'left' }}>City</th>
                                    <th>State</th>
                                    <th>Postal Code</th>
                                    <th style={{ textAlign: 'left' }}>Note</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.addresses.map(address =>
                                    <tr key={address.id}>
                                        <td style={{ textAlign: 'left' }}>{address.typeName}</td>
                                        <td style={{ textAlign: 'left' }}>
                                            {address.label}
                                            {address.typeId == OpoAddressType.StrongBox && (
                                                <div className='muted'>{address.firstName} {address.lastName} &middot; {address.shipToPhoneNumber}</div>
                                            )}
                                        </td>
                                        <td style={{ textAlign: 'left' }}>
                                            {address.addressLine3 && <div className='muted'>Attn: {address.addressLine3}</div>}
                                            <div>{address.addressLine1}</div>
                                            {address.addressLine2 && <div>{address.addressLine2}</div>}
                                        </td>
                                        <td style={{ textAlign: 'left' }}>{address.city}</td>
                                        <td>{address.stateAbbreviation}</td>
                                        <td>{address.postalCode}</td>
                                        <td style={{ textAlign: 'left', maxWidth: 220, whiteSpace: 'normal' }} className='muted'>{address.note}</td>
                                        <td>
                                            {canManage && (
                                                <div className='btn-group'>
                                                    <button type='button' className='btn btn-default' onClick={() => setEditing({ show: true, address })}>Edit</button>
                                                    <button type='button' className='btn btn-default' onClick={() => setDeleting(address)}>Delete</button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                )}

                                {data.addresses.length == 0 && (
                                    <tr>
                                        <td colSpan={8}>No addresses have been added yet</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </SettingsTableCard>
            )}

            {data && (
                <AddressModal
                    show={editing.show}
                    address={editing.address}
                    availableTypes={availableTypes}
                    allTypes={data.addressTypes}
                    states={data.states}
                    onSave={handleSave}
                    onValidateAddress={validateAddress}
                    onCancel={() => setEditing({ show: false, address: null })}
                />
            )}

            <ConfirmModal
                show={deleting != null}
                title='Delete Address'
                message={<>Delete the <strong>{deleting?.typeName}</strong> address ({deleting?.addressLine1}, {deleting?.city})?</>}
                confirmText='Delete'
                onConfirm={handleDelete}
                onCancel={() => setDeleting(null)}
            />
        </SettingsPageWrapper>
    );
}

export default OpoAddresses;
